// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// Utils
import { getEMRErrorMessage } from "src/utils/emr/emrErrorHelper";

// Types and API
import { EMRPacServerJobPayload } from "../../types";
import { updateEMRPacServer } from "../../api";

type UpdateEMRPacServerPayload = {
  pacServerId: string;
  name: string;
  deviceKey: string;
  jobs: Array<EMRPacServerJobPayload>;
};

export const useUpdateEMRPacServer = (
  clinicId: string | undefined,
  hubId: string | undefined
) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async ({
      pacServerId,
      ...payload
    }: UpdateEMRPacServerPayload) => {
      if (!clinicId) throw new Error();
      const token = await getToken();
      return updateEMRPacServer(token, clinicId, pacServerId, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["emr-pac-servers", clinicId, hubId],
      });
      queryClient.invalidateQueries({
        queryKey: ["emr-hub-connections", clinicId, hubId],
      });
      setSnackbarInfo({
        variant: "success",
        message: t("PAC server updated successfully!"),
      });
    },
    onError: (error) => {
      setSnackbarInfo({
        variant: "error",
        message: getEMRErrorMessage(
          error,
          t("PAC server could not be updated!"),
          t
        ),
      });
    },
  });
};
